import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';
import CustomDropdown from 'components/NewDesign/CustomDropdown/CustomDropdown';
import CustomInput from 'components/common/CustomInput';
import CustomCheckbox from 'components/common/CustomCheckbox';
import {
  LABEL_SHEETS,
  labelsPerSheet,
  generateAthleteLabelsPdf,
} from 'utils/athleteLabels';
import styles from './PrintLabelsModal.module.css';
import { groupingBasisOf } from 'utils/groupLabel';

const SHEET_OPTIONS = LABEL_SHEETS.map((s) => ({
  value: s.id,
  label: s.label,
}));

// Downloads a PDF of Data Matrix bib-number labels laid out for Avery label
// sheets. Callers pass either a flat `athletes` list (the Club Athletes page,
// where rows are selected in the table) or `groups` (the Meet Registrations
// page, where the dialog itself offers an age-group multi-select). Athletes
// without a bib number can't get a scannable label, so they are listed in a
// warning and skipped.
const PrintLabelsModal = ({ isOpen, athletes = [], groups, onClose }) => {
  // How the selected club groups its athletes: a label reads "Age 11", or "Year 5" for a club that groups by
  // year level.
  const groupingBasis = useSelector((state) => groupingBasisOf(state.user?.selectedClub));
  const [sheetId, setSheetId] = useState(LABEL_SHEETS[0].id);
  const [startPosition, setStartPosition] = useState('1');
  const [copies, setCopies] = useState('1');
  const [errors, setErrors] = useState({});
  // All groups start selected — "print everything" is the common case and
  // deselecting a few is easier than building a selection from nothing.
  const [selectedGroupIds, setSelectedGroupIds] = useState(
    () => new Set((groups || []).map((g) => g.id))
  );

  const sheet =
    LABEL_SHEETS.find((s) => s.id === sheetId) || LABEL_SHEETS[0];
  const perPage = labelsPerSheet(sheet);

  const toggleGroup = (id) => {
    setSelectedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const allGroupsSelected = !!groups && selectedGroupIds.size === groups.length;
  const toggleAllGroups = () => {
    setSelectedGroupIds(
      allGroupsSelected ? new Set() : new Set(groups.map((g) => g.id))
    );
  };

  const { eligible, skipped } = useMemo(() => {
    const pool = groups
      ? groups
          .filter((g) => selectedGroupIds.has(g.id))
          .flatMap((g) => g.athletes)
      : athletes;
    const eligibleList = [];
    const skippedList = [];
    pool.forEach((a) => {
      (Number(a.clubAthleteNumber) > 0 ? eligibleList : skippedList).push(a);
    });
    return { eligible: eligibleList, skipped: skippedList };
  }, [athletes, groups, selectedGroupIds]);

  // Live summary — tolerant of in-progress typing in the number fields.
  const copiesNum = Math.max(1, parseInt(copies, 10) || 1);
  const startNum = Math.min(
    perPage,
    Math.max(1, parseInt(startPosition, 10) || 1)
  );
  const labelCount = eligible.length * copiesNum;
  const sheetCount =
    labelCount > 0 ? Math.ceil((startNum - 1 + labelCount) / perPage) : 0;

  const skippedNames = skipped.map((a) => a.athleteName);
  const warningText =
    skipped.length > 0
      ? `${skipped.length} selected ${skipped.length === 1 ? 'athlete has' : 'athletes have'} no bib number and will be skipped: ${skippedNames
          .slice(0, 5)
          .join(', ')}${skippedNames.length > 5 ? ` and ${skippedNames.length - 5} more` : ''}.`
      : '';

  const handleDownload = () => {
    const next = {};
    const start = Number(startPosition);
    const copiesValue = Number(copies);
    if (!Number.isInteger(start) || start < 1 || start > perPage) {
      next.startPosition = `Must be between 1 and ${perPage}`;
    }
    if (!Number.isInteger(copiesValue) || copiesValue < 1 || copiesValue > 10) {
      next.copies = 'Must be between 1 and 10';
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    if (groups && selectedGroupIds.size === 0) {
      toast.error('Select at least one age group.');
      return;
    }
    if (eligible.length === 0) {
      toast.error('None of the selected athletes have a bib number.');
      return;
    }

    try {
      const date = new Date().toISOString().slice(0, 10);
      const result = generateAthleteLabelsPdf({
        athletes: eligible,
        sheet,
        startPosition: start,
        copies: copiesValue,
        fileName: `athlete-labels-${date}.pdf`,
        groupingBasis,
      });
      toast.success(
        `Downloaded ${result.labelCount} ${result.labelCount === 1 ? 'label' : 'labels'} across ${result.sheetCount} ${result.sheetCount === 1 ? 'sheet' : 'sheets'}.`
      );
      onClose();
    } catch (err) {
      console.error('[PrintLabelsModal] generate error:', err);
      toast.error('Could not generate the label PDF.');
    }
  };

  return (
    <CustomNewConfirm
      isOpen={isOpen}
      title="Print Barcode Labels"
      description={
        <>
          Downloads a PDF of bib-number barcode labels formatted for Avery label
          sheets.{' '}
          <strong>
            Print it at 100% (Actual size) so the labels line up with the sheet.
          </strong>
        </>
      }
      confirmButtonText="Download PDF"
      confirmButtonVariant="info"
      onConfirm={handleDownload}
      onClose={onClose}
      showImpactSummary
      impactItems={[
        { label: 'Athletes', value: String(eligible.length) },
        { label: 'Labels', value: String(labelCount) },
        { label: 'Sheets', value: String(sheetCount) },
      ]}
      showWarning={skipped.length > 0}
      warningText={warningText}
    >
      <div>
        {groups && (
          <div className={styles.groupSection}>
            <span className={styles.groupSectionLabel}>Age groups</span>
            <div className={styles.groupGrid}>
              <div className={styles.selectAllRow}>
                <CustomCheckbox
                  label={`All age groups (${groups.length})`}
                  checked={allGroupsSelected}
                  onChange={toggleAllGroups}
                />
              </div>
              {groups.map((g) => (
                <CustomCheckbox
                  key={g.id}
                  label={`${g.label} (${g.athletes.length})`}
                  checked={selectedGroupIds.has(g.id)}
                  onChange={() => toggleGroup(g.id)}
                />
              ))}
            </div>
            {selectedGroupIds.size === 0 && (
              <div className={styles.groupError}>
                Select at least one age group
              </div>
            )}
          </div>
        )}
        <div style={{ marginBottom: 16 }}>
          <CustomDropdown
            label="Label sheet"
            name="sheetId"
            value={sheetId}
            options={SHEET_OPTIONS}
            onChange={(name, value) => setSheetId(value)}
          />
        </div>
        <div className="twoColumnRow">
          <div className="flex1">
            <CustomInput
              label="Start at label position"
              type="number"
              name="startPosition"
              value={startPosition}
              min="1"
              max={String(perPage)}
              step="1"
              error={errors.startPosition}
              onChange={(e) => setStartPosition(e.target.value)}
            />
          </div>
          <div className="flex1">
            <CustomInput
              label="Copies per athlete"
              type="number"
              name="copies"
              value={copies}
              min="1"
              max="10"
              step="1"
              error={errors.copies}
              onChange={(e) => setCopies(e.target.value)}
            />
          </div>
        </div>
      </div>
    </CustomNewConfirm>
  );
};

PrintLabelsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  // Flat, pre-selected athlete rows (Club Athletes page). Ignored when
  // `groups` is provided.
  athletes: PropTypes.array,
  // Age-group buckets for the in-dialog multi-select (Meet Registrations
  // page): [{ id, label, athletes: [...] }]
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      athletes: PropTypes.array.isRequired,
    })
  ),
  onClose: PropTypes.func.isRequired,
};

export default PrintLabelsModal;
